/**
 * PROJECT-POPULATION-IMAGES.MODAL
 * Manage groups of populations sharing one welcome image (quickvote intro screen)
 *
 * The image of each group is a real upload, stored in its own "ProjectPopulationImages"
 * space (distinct from "Projects"/"Accounts", so it can't collide with existing images).
 */
import { useEffect, useRef, useState } from "react"
import { withTranslation, WithTranslation } from "react-i18next"
import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import Modal from "@/components/modal"
import ListItem from "@/components/list-item"
import ImagePicker from "@/components/image-picker"
import CardButton from "@/components/card-button"
import Chip from "@/components/chip"
import Link from "@/components/link"
import Space from "@/components/space"
import PopulationImageGroupRow from "@/components/population-image-group-row"
import SelectPopulationsModal from "./select-populations.modal"
import { Population } from "@/redux/population.types"
import { ProjectPopulationImageGroup } from "@/redux/project.types"
import env from "@/env"

const POPULATION_IMAGE_MODEL: string = "ProjectPopulationImages"

interface OwnProps {
  groups: ProjectPopulationImageGroup[]
  onClose: Function
  onSave: (groups: ProjectPopulationImageGroup[]) => void
}

type Props = OwnProps & WithTranslation

function ProjectPopulationImagesModal(props: Props) {
  const { t } = props

  const [groups, setGroups] = useState<ProjectPopulationImageGroup[]>(props.groups)
  const [isSelectingPopulations, setIsSelectingPopulations] = useState(false)
  const [draftPopulations, setDraftPopulations] = useState<Population[]>([])
  const [draftGroup, setDraftGroup] = useState(() => new ProjectPopulationImageGroup())
  const [draftHasImage, setDraftHasImage] = useState(false)
  const [isPickingDraftImage, setIsPickingDraftImage] = useState(false)
  const draftPickerRef = useRef<HTMLDivElement>(null)

  function getUploadedImageUrl(id: string): string {
    return env.REACT_APP_URL_SPACE.concat("/", POPULATION_IMAGE_MODEL, "/", id, ".png?", Date.now().toString())
  }

  //Open the native file dialog as soon as the picker shows up (skip the extra "Charger une image" click)
  useEffect(() => {
    if (isPickingDraftImage) {
      const input = draftPickerRef.current?.querySelector<HTMLInputElement>("#avatar-" + draftGroup.id)
      const onCancel = () => setIsPickingDraftImage(false)
      input?.addEventListener("cancel", onCancel)
      input?.click()
      return () => input?.removeEventListener("cancel", onCancel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPickingDraftImage])

  //As soon as the draft has both populations and an image, commit it as a new group
  useEffect(() => {
    if (draftPopulations.length && draftHasImage) {
      setGroups((current) =>
        current.concat([
          new ProjectPopulationImageGroup({
            id: draftGroup.id,
            populations: draftPopulations,
            imageUrl: getUploadedImageUrl(draftGroup.id)
          })
        ])
      )
      setDraftPopulations([])
      setDraftHasImage(false)
      setDraftGroup(new ProjectPopulationImageGroup())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftPopulations, draftHasImage])

  function removeDraftPopulation(populationId: string) {
    setDraftPopulations(draftPopulations.filter((population) => population.id !== populationId))
  }

  function removeGroup(groupId: string) {
    setGroups(groups.filter((group) => group.id !== groupId))
  }

  //A draft with only a population OR only an image selected would be silently lost on save
  const hasIncompleteDraft: boolean = draftPopulations.length > 0 !== draftHasImage

  return (
    <Modal
      isFullScreen
      className="population-images-modal"
      isCloseButtonVisible
      onClose={() => props.onClose()}
      onNext={() => props.onSave(groups)}
      onNextCta={t("utils_save")}
      nextDisabled={hasIncompleteDraft}
      nextDisabledTooltip={hasIncompleteDraft ? t("project_image_population_incomplete_draft") : undefined}
      title={t("project_image_population_modal_title")}>
      {isSelectingPopulations && (
        <SelectPopulationsModal
          selectedPopulations={draftPopulations}
          onClose={() => setIsSelectingPopulations(false)}
          onNext={(populations: Population[]) => {
            setDraftPopulations(populations)
            setIsSelectingPopulations(false)
          }}
        />
      )}

      <div
        style={{
          maxWidth: 460,
          border: "1px solid #ececec",
          borderRadius: 8,
          padding: 24
        }}>
        <div className="flex flex-wrap" style={{ columnGap: 6, rowGap: 12, alignItems: "center" }}>
          {draftPopulations.map((population) => (
            <Chip key={population.id} onDelete={() => removeDraftPopulation(population.id)}>
              {population.name}
            </Chip>
          ))}

          <Link isWithoutMargin onClick={() => setIsSelectingPopulations(true)}>
            {draftPopulations.length
              ? t("project_image_population_select_more")
              : t("project_image_population_select")}
          </Link>
        </div>

        <div className="height-20" />

        {isPickingDraftImage ? (
          <div ref={draftPickerRef} className="population-image-picker flex flex-dcol" style={{ width: 140 }}>
            <ImagePicker
              id={draftGroup.id}
              square
              imageId={draftHasImage ? draftGroup.id : null}
              model={POPULATION_IMAGE_MODEL}
              onDelete={() => setDraftHasImage(false)}
              onSave={() => {
                setDraftHasImage(true)
                setIsPickingDraftImage(false)
              }}
            />

            <Space />

            <div className="grey-t flex">
              <Space />
              <u className="_hover grey-t" onClick={() => setIsPickingDraftImage(false)}>
                {t("utils_cancel")}
              </u>
              <Space />
            </div>
          </div>
        ) : (
          <div style={{ marginLeft: -14 }}>
            <CardButton
              title={t("image_load")}
              image={draftHasImage ? getUploadedImageUrl(draftGroup.id) : undefined}
              icon={draftHasImage ? undefined : faPlus}
              onClick={() => !draftHasImage && setIsPickingDraftImage(true)}
              actions={
                draftHasImage
                  ? [
                      { id: "edit", icon: faPen, label: t("utils_edit") },
                      { id: "delete", icon: faTrash, label: t("utils_delete") }
                    ]
                  : undefined
              }
              onAction={(actionId) => {
                if (actionId === "edit") setIsPickingDraftImage(true)
                if (actionId === "delete") setDraftHasImage(false)
              }}
            />
          </div>
        )}

      </div>

      <div className="height-40" />

      {groups.length > 0 && (
        <div className="list-container" style={{ border: "1px solid #ececec", borderRadius: 8, overflow: "hidden" }}>
          {groups.map((group) => (
            <ListItem key={group.id}>
              <PopulationImageGroupRow group={group} onRemove={() => removeGroup(group.id)} />
            </ListItem>
          ))}
        </div>
      )}
    </Modal>
  )
}

export default withTranslation()(ProjectPopulationImagesModal)
